import {
  COMPACT_TOKEN_MANTISSA_ROUNDED_UP,
  COMPACT_TOKEN_UNITS,
} from "../constants/token-usage";

// NOTE: Token count formatting helpers shared by the web and mobile token-usage
// components so they render numbers identically (and from a single source of
// truth). Used by summary cards, accordion stats, and inline token counts.

// NOTE: Exact locale-aware formatting. Falls back to "0" for null/undefined so
// callers never have to special-case missing values before rendering.
export const formatTokenCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "0";
  return value.toLocaleString();
};

const EXACT_FORMATTING_MAX = 100;

// NOTE: A trailing ".0" is noise — 10_000 reads "10K", not "10.0K".
const formatOneDecimal = (value: number): string => {
  const fixed = value.toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
};

// NOTE: Compact notation with up to one decimal: 150 → "150", 1_234 → "1.2K",
// 1_234_567 → "1.2M", 2_000_000_000 → "2B". ASCII units, so it reads the same
// on every runtime — used directly where the output must not vary, and as the
// fallback for runtimes without compact ICU data (see below).
const formatCompactAscii = (value: number): string => {
  const absoluteValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  for (const [unitIndex, unit] of COMPACT_TOKEN_UNITS.entries()) {
    if (absoluteValue < unit.threshold) {
      continue;
    }

    const mantissa = formatOneDecimal(absoluteValue / unit.threshold);

    // NOTE: 999_999 / 1_000 rounds to "1000", which reads as the unit above
    // rather than 1000 of this one, so borrow it: "1M", not "1000K".
    if (mantissa === COMPACT_TOKEN_MANTISSA_ROUNDED_UP && unitIndex > 0) {
      return `${sign}1${COMPACT_TOKEN_UNITS[unitIndex - 1].suffix}`;
    }

    return `${sign}${mantissa}${unit.suffix}`;
  }

  return `${sign}${Math.round(absoluteValue)}`;
};

// NOTE: Hermes on iOS ships without the compact ICU data and silently falls back
// to grouped output, so the same card read "9K / 10K tokens" on Android and
// "9,000 / 10,000 tokens" on iOS (NSAI-4725, caught on device). Probe once by
// comparing compact against plain formatting of the same value: a runtime with
// the data renders them differently ("1K" vs "1,000"), one without renders both
// the same. Locale-agnostic, so it holds for the non-ASCII digits ar / ru use.
const resolveCompactFormatter = (): Intl.NumberFormat | null => {
  try {
    const compactFormatter = new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    });
    const plainFormatter = new Intl.NumberFormat(undefined);
    const probe = COMPACT_TOKEN_UNITS[COMPACT_TOKEN_UNITS.length - 1].threshold;

    return compactFormatter.format(probe) === plainFormatter.format(probe)
      ? null
      : compactFormatter;
  } catch {
    return null;
  }
};

const compactFormatter = resolveCompactFormatter();

// NOTE: The usage-threshold card's tilde-free figures. Locale-aware where the
// runtime can be ("1,2 тыс."), ASCII units where it cannot.
export const formatUsageTokens = (value: number): string =>
  compactFormatter ? compactFormatter.format(value) : formatCompactAscii(value);

// NOTE: Same compaction, with a "~" prefix to signal the rounding. Values under
// 100 stay exact, since rounding them would hide a difference the reader cares
// about: 99 → "99", 150 → "~150", 150_654 → "~150.7K". Deliberately on the ASCII
// path: these figures sit in dense stat grids where a localized unit ("тыс.")
// would wrap the cell.
export const formatTokenCountCompact = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return "0";
  if (Math.abs(value) < EXACT_FORMATTING_MAX) return value.toLocaleString();
  return `~${formatCompactAscii(value)}`;
};
